import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { SpaceMetadataQuery } from './SpaceMetadataQuery.graphql.ts';
import type {
	SpaceMetadataQueryVariables,
	SpaceMetadataQuery as SpaceMetadataQueryType,
} from './__types__/SpaceMetadataQuery.ts';

/**
 * SSOT payload shared between companyHubAnalyticsHandleEventCaught and linksMenuExtension to create company hub analytic viewed event
 * payload conforms to a particular format required for the analytics service to ingest and publish these view events back to admins on company hub analytics
 */
export const generateViewEventPayload = (
	activationId: string,
	cloudId: string,
	objectId?: string,
	spaceId?: string,
): {
	type: string;
	data: {
		action: string;
		actionSubject: string;
		source: string;
		objectType: string;
		objectId: string | undefined;
		attributes: {
			activationId: string;
			spaceId: string | undefined;
			spaceType: string;
			pageType: string;
			cloudId: string;
		};
	};
} => ({
	type: 'sendTrackEvent',
	data: {
		action: 'viewed',
		actionSubject: 'companyHubLink',
		source: 'companyHub',
		objectType: 'page',
		objectId,
		attributes: {
			activationId,
			spaceId,
			spaceType: 'system',
			pageType: 'page',
			cloudId,
		},
	},
});

/**
 * SSOT payload shared between companyHubAnalyticsHandleEventCaught and linksMenuExtension to create company hub analytic clicked event
 * payload conforms to a particular format required for the analytics service to ingest and publish these click events back to admins on company hub analytics
 */
export const generateClickEventPayload = (
	activationId: string,
	cloudId: string,
	objectId?: string,
	spaceId?: string,
): {
	type: string;
	data: {
		action: string;
		actionSubject: string;
		source: string;
		objectType: string | null;
		objectId: string | undefined;
		attributes: {
			activationId: string;
			spaceId: string | undefined;
			spaceType: string;
			pageType: string | null;
			cloudId: string;
		};
	};
} => ({
	type: 'sendTrackEvent',
	data: {
		action: 'clicked',
		actionSubject: 'companyHubLink',
		source: 'companyHub',
		objectType: objectId ? 'page' : null,
		objectId,
		attributes: {
			activationId,
			spaceId,
			spaceType: 'system',
			pageType: objectId ? 'page' : null,
			cloudId,
		},
	},
});

/**
 * linking platform analytic events always send the spaceId as destinationContainerId and spaceId as the objectId when the link points to space homepage.
 * However, we need the objectId to always be the page id, otherwise analytics dashboard would not ingest the numbers correctly.
 * if so we return homepageId. if not, we can simply return objectId.
 * destinationContainerId === objectId checks for if the link clicked event was a click on the space homepage, in which case we need to fetch the corresponding pageId
 */
export const getClickAndViewEventObjectIdFromLinkData = async (
	objectId: string,
	destinationContainerId?: string,
): Promise<string | undefined> => {
	if (destinationContainerId === objectId) {
		const { data } = await queryInterop<SpaceMetadataQueryType, SpaceMetadataQueryVariables>({
			query: SpaceMetadataQuery,
			variables: {
				id: destinationContainerId,
			},
		});
		const homepageId = data?.spaces?.nodes?.[0]?.homepage?.id;
		if (!homepageId) {
			return;
		}
		return homepageId;
	}
	return objectId;
};
